import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useLingui } from '@lingui/react';
import type { ShiftTypeValue } from '@/types/shifts';
import { ShiftType } from '@/types/shifts';

interface ShiftTypeBadgeProps {
  /**
   * The wire types expose this as a free `string` (#1156), so the prop takes
   * one too and the `default:` arm below renders the neutral badge for any
   * value outside the known set — exactly what it did before.
   */
  type: ShiftTypeValue | (string & {});
  className?: string;
}

function getTypeConfig(type: ShiftTypeValue | (string & {})) {
  switch (type) {
    case ShiftType.REGULAR:
      return { variant: 'outline' as const, className: '' };
    case ShiftType.OVERTIME:
      return {
        variant: 'default' as const,
        className: 'border-transparent bg-warning text-warning-foreground hover:bg-warning/90',
      };
    case ShiftType.ON_CALL:
      return { variant: 'secondary' as const, className: '' };
    default:
      return { variant: 'outline' as const, className: '' };
  }
}

export function ShiftTypeBadge({ type, className }: ShiftTypeBadgeProps) {
  const { i18n } = useLingui();
  const config = getTypeConfig(type);

  // Partial: an unknown wire value falls back to the raw string, matching the
  // neutral badge the config switch already returns for it.
  const labels: Partial<Record<ShiftTypeValue | (string & {}), string>> = {
    [ShiftType.REGULAR]: i18n._({
      id: 'shifts.type.regular',
      message: 'Regulär',
    }),
    [ShiftType.OVERTIME]: i18n._({
      id: 'shifts.type.overtime',
      message: 'Überstunden',
    }),
    [ShiftType.ON_CALL]: i18n._({
      id: 'shifts.type.onCall',
      message: 'Bereitschaft',
    }),
  };

  return (
    <Badge
      variant={config.variant}
      className={cn(config.className, className)}
      aria-label={i18n._({ id: 'shifts.type.ariaLabel', message: 'Shift type: {type}', values: { type: labels[type] ?? type } })}
    >
      {labels[type] ?? type}
    </Badge>
  );
}
